/**
 * Feature Flags Context
 * Feature: 058-markdown-markup-blocks
 *
 * Provides feature flag state management with localStorage persistence.
 * Enables experimental features to be toggled on/off via dev menu.
 */

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import logger from '@/utils/logger';

/**
 * Feature flags configuration
 */
export interface FeatureFlags {
  /** Use GraphQL for data fetching instead of REST (experimental) */
  useGraphQL: boolean;
}

/**
 * Feature flags context value
 */
export interface FeatureFlagsContextValue {
  /** Current feature flag states */
  flags: FeatureFlags;

  /** Toggle a feature flag on/off */
  toggleFlag: (flagName: keyof FeatureFlags) => void;
}

/**
 * localStorage key for persisting feature flags
 */
export const FEATURE_FLAGS_STORAGE_KEY = '206mp_feature_flags';

/**
 * Default feature flag values
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  useGraphQL: false, // GraphQL migration - default OFF (gradual rollout)
};

/**
 * Feature flags context
 */
const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

/**
 * Feature flags provider component
 *
 * Wraps app with feature flag context, persists to localStorage
 *
 * @example
 * <FeatureFlagsProvider>
 *   <App />
 * </FeatureFlagsProvider>
 */
export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlags>(() => {
    try {
      const stored = localStorage.getItem(FEATURE_FLAGS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to handle new flags
        return { ...DEFAULT_FEATURE_FLAGS, ...parsed };
      }
      return DEFAULT_FEATURE_FLAGS;
    } catch (error) {
      logger.warn('FeatureFlags', 'Failed to load flags from localStorage:', error);
      return DEFAULT_FEATURE_FLAGS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(FEATURE_FLAGS_STORAGE_KEY, JSON.stringify(flags));
      logger.debug('FeatureFlags', 'Persisted flags to localStorage:', flags);
    } catch (error) {
      logger.warn('FeatureFlags', 'Failed to persist flags to localStorage:', error);
    }
  }, [flags]);

  const toggleFlag = useCallback((flagName: keyof FeatureFlags) => {
    setFlags((prev) => {
      const newFlags = { ...prev, [flagName]: !prev[flagName] };
      logger.info('FeatureFlags', `Toggled ${flagName}:`, prev[flagName], '→', newFlags[flagName]);
      return newFlags;
    });
  }, []);

  return (
    <FeatureFlagsContext.Provider value={{ flags, toggleFlag }}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Hook to access feature flags
 *
 * @returns Current flags and toggle function
 * @throws Error if used outside FeatureFlagsProvider
 *
 * @example
 * function CommentForm() {
 *   const { flags } = useFeatureFlags();
 *
 *   if (flags.useGraphQL) {
 *     // Use GraphQL queries
 *   }
 * }
 */
export function useFeatureFlags(): FeatureFlagsContextValue {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider');
  }
  return context;
}
